<template>
  <div class="input-text" :class="{ fill, difference }">
    <label v-if="label || forReport || forState">
      <span v-if="label" :class="{ invalid }" v-html="label" />
      <Icon v-if="forReport" name="fieldsSAV" />
      <Icon v-if="forState" name="fieldsState" />
      <Tooltip v-if="tooltip" icon="info" :content="tooltip" />
    </label>
    <div>
      <div class="icon" v-if="icon">
        <Icon :name="icon" />
      </div>
      <input
        ref="input"
        :id="iuid"
        :class="{ invalid }"
        :type="inputType"
        :placeholder="placeholder"
        :value="value"
        :autofocus="autofocus"
        @focus="handleFocus($event)"
        @blur="handleBlur($event)"
        @input="handleInput($event)"
        :disabled="disabled"
      />
      <template v-if="type === 'password'">
        <div class="visiblity" @click="toggleVisibility">
          <Icon :name="itype === 'password' ? 'passwordShow' : 'passwordHide'" />
        </div>
      </template>
    </div>
    <span v-if="errorMessage !== null && vuelidate && invalid" class="error">{{ errorMessage }}</span>
    <span v-else-if="hint" class="hint" v-html="hint" />
    <template v-if="difference">
      <button class="adopt" @click.stop.prevent="handleAdopt(true)">{{ $t('review.diff.adopt') }}</button>
      <button class="adopt" @click.stop.prevent="handleAdopt(false)">{{ $t('review.diff.ignore') }}</button>
    </template>
  </div>
</template>

<script>
  import Icon from '@/components/ui/Icon';
  import Tooltip from '@/components/ui/Tooltip';

  const STRING_EMPTY = '';
  const MASK_PATTERNS = {
    index: ['n', 'a', '?', '*'],
    n: /[0-9]/,
    a: /[a-zA-Z]/,
    '?': /[A-Za-z0-9]/,
    '*': /\S/,
  };
  let uid = 0;

  export default {
    name: 'TextInput',
    components: {
      Tooltip,
      Icon,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      value: {
        type: String,
        default: null,
      },
      type: {
        type: String,
        default: 'text',
      },
      label: {
        type: String,
        default: null,
      },
      tooltip: {
        type: String,
        default: null,
      },
      placeholder: {
        type: String,
        default: null,
      },
      hint: {
        type: String,
        default: null,
      },
      autofocus: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: String,
        default: null,
      },
      mask: {
        type: String,
        default: null,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      forState: {
        type: Boolean,
        default: false,
      },
      forReport: {
        type: Boolean,
        default: false,
      },
      vuelidate: {
        type: Object,
        default: null,
      },
      errorMessage: {
        type: [String],
        default: null,
      },
      //----------------------------------
      // CSS Related Properties
      //----------------------------------
      fill: {
        type: Boolean,
        default: false,
      },
      difference: {
        type: Boolean,
        default: false,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      uid += 1;
      return {
        iuid: `input-text-${uid}`,
        blurred: false,
        itype: this.type,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      inputType() {
        return this.itype || (this.mask === null || (/[a*?]/.test(this.mask)) ? 'text' : 'tel');
      },
      invalid() {
        const $v = this.vuelidate;
        return (this.error || ($v && $v.$dirty && $v.$invalid));
      },
      dirty() {
        return this.value !== null;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      type(val) {
        this.itype = val;
      },
      value() {
        const $v = this.vuelidate;
        if ($v && $v.$dirty && this.blurred) {
          $v.$touch();
        }
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      toggleVisibility() {
        const itype = this.itype;
        this.itype = (itype === 'password') ? 'text' : 'password';
      },
      focus() {
        this.$refs.input.focus();
      },

      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleFocus(evt) {
        const value = this.value;
        if (value === 'k.A.') {
          const { input } = this.$refs;
          input.select();
        }
        this.$emit('focus', evt);
      },
      handleBlur(evt) {
        const $vuelidate = this.vuelidate;
        if ($vuelidate) {
          this.blurred = true;
          if (this.dirty) {
            $vuelidate.$touch();
          }
        }
        this.$emit('blur', evt);
      },
      handleInput(evt) {
        evt.preventDefault();
        evt.stopPropagation();

        let out = STRING_EMPTY;
        const { input } = this.$refs;
        const mask = (this.mask) ? this.mask.split(out) : null;

        if (mask) {
          const values = input.value.split(STRING_EMPTY);
          for (let i = 0; i < mask.length; i += 1) {
            let expr = null;
            const char = mask[i];
            if (MASK_PATTERNS.index.includes(char)) {
              expr = MASK_PATTERNS[char];
            }

            if (values.length > 0) {
              if (expr && expr.test(values[0])) {
                out += values.shift();
              } else if (!expr && values[0] === char) {
                out += values.shift();
              } else if (!expr && !/[na*?]/.test(char)) {
                out += char;
              } else {
                break;
              }
            } else {
              break;
            }
          }
          input.value = out;
        }
        out = input.value;
        const len = out.length;
        if (mask && len > 0) {
          this.$nextTick(() => {
            input.setSelectionRange(len, len);
          });
        }
        const value = (len > 0) ? out : null;
        this.search = value;
        /**
         * <abbr badge="sync" title="Vue Sync Property"/>
         * Property-Sync Event - emitted when `value` changes.
         * @type {String}
         */
        this.$emit('update:value', value);
        /**
         * Classic input event emitted when `value` changes.
         * @type {String}
         */
        this.$emit('input', value, evt);
      },
      handleAdopt(accept) {
        this.$emit('adopt', { accept, value: this.value });
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/animations';
@import '~scss/mixins';
@import '~scss/variables';

.input-text {
  display: inline-block;
  position: relative;

  & > label {
    display: block;
    @include font-body-medium;
    color: $color-black-solid-100;
    margin-bottom: 4px;

    & > svg {
      position: relative;
      top: 2px;
      width: 14px;
      height: 14px;
      margin-left: 4px;
    }

    & > .comp-tooltip {
      margin-left: 8px;
    }

    .invalid {
      color: $color-signal-red;
    }
  }

  & > span.hint {
    display: block;
    margin-top: 4px;
    @include font-body-small;
    color: $color-black-alpha-60;
  }
  & > span.error {
    display: block;
    margin-top: 4px;
    @include font-body-small;
    color: $color-signal-red;
  }

  & > button.adopt {
    display: inline-block;
    outline: 0;
    border: 0;
    cursor: pointer;
    background: transparent;
    text-decoration: underline;
    margin: 8px 0 0;
    @include font-body-small;
    color: $color-black-alpha-60;
  }

  & > div {
    display: inline-flex;
    height: 100%;

    input[type=text], input[type=password], input[type=email] {
      width: 100%;
      padding: 13px 11px;
      height: 100%;
      border-radius: $default-border-radius;
      border: 1px solid $color-black-alpha-40;
      outline: 0;
      @include font-body-medium;
      color: $color-black-solid-100;
      transition: border $default-transition;

      &::placeholder {
        @include font-body-medium;
        color: $color-black-alpha-60;
      }

      &.invalid {
        border: 1px solid red !important;
      }
    }

    input[type=password] {
      padding-right: 48px;
    }

    & > div.visiblity {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      right: 0;
      bottom: 0;
      width: 48px;
      height: 46px;
      cursor: pointer;

      svg {
        width: 24px;
        color: $color-black-alpha-40;
        transition: color $default-transition;
      }
    }

    div.icon {
      position: relative;
      display: inline-flex;
      align-items: center;
      border: 1px solid $color-black-alpha-40;
      border-radius: $default-border-radius 0 0 $default-border-radius;
      border-right: 0;
      padding: 0 12px;

      & > svg {
        width: 18px;
        height: 18px;
      }

      & + input[type=text], input[type=password] {
        border-radius: 0 $default-border-radius $default-border-radius 0;
        border-left: 0;
        padding-left: 0;
      }
    }

    &:focus-within {
      input[type=text], input[type=password], input[type=email] {
        border: 1px solid $color-blue-alpha-80;
      }

      div.icon {
        border: 1px solid $color-blue-alpha-80;
        border-right: 0;
      }

    }
  }
  &.fill {
    width: 100%;
    & > div {
      display: flex;
    }
  }

  &.difference {
    & > div {
      input[type=text], input[type=password], input[type=email] {
        box-shadow: 0 0 5px 3px rgba(214, 16, 16, 0.8);
      }
    }
  }
}
</style>
